import React from 'react';
import styled from 'styled-components';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import TruncatedText from '@spark/ui-components/components/TanStackTable/components/TruncatedText';

const getDisponibilidadeTmsBadgeVariant = value => (value === 'Disponivel' ? 'success' : 'danger');

const getStatusPatioBadgeVariant = row => row?.original?.statusPatioVariant || 'secondary';

const CompactBadge = ({ label, variant }) => (
  <CompactBadgeWrap title={label} $variant={variant}>
    {label}
  </CompactBadgeWrap>
);

const HeaderLabel = ({ text }) => (
  <HeaderLabelText title={text}>{text}</HeaderLabelText>
);

export const getDisponibilidadeFrotaColumns = ({
  onPositionClick,
  showPositionColumn = false,
  showPlotagemColumn = false,
  showTipoOperacaoColumn = true,
  getPositionStatus,
  getPositionTooltip
} = {}) => [
  ...(showPositionColumn ? [{
    id: 'posicao',
    header: '',
    accessorKey: 'posicao',
    size: 40,
    enableSorting: false,
    cell: ({ row }) => {
      const status = getPositionStatus?.(row.original) || 'unknown';
      const tooltip = getPositionTooltip?.(row.original) || 'Ver posicao';
      return (
      <PositionButton
        type="button"
        title={tooltip}
        aria-label="Ver posicao da frota"
        $status={status}
        onClick={event => {
          event.stopPropagation();
          onPositionClick?.(row.original);
        }}
      >
        <LocationOnIcon style={{ fontSize: 15 }} />
      </PositionButton>
      );
    }
  }] : []),
  {
    id: 'frota',
    header: 'Frota',
    accessorKey: 'frota',
    size:75,
    enableSorting: true,
    cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={7} />
  },
  {
    id: 'veiculo',
    header: 'Veiculo',
    accessorKey: 'veiculo',
    size: 100,
    enableSorting: true,
    cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={8} />
  },
  {
    id: 'gestor',
    header: 'Gestor',
    accessorKey: 'gestor',
    size: 150,
    enableSorting: true,
    cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={14} />
  },
  {
    id: 'modeloVeicular',
    header: () => <HeaderLabel text="Modelo Veicular" />,
    accessorKey: 'modeloVeicular',
    size: 190,
    enableSorting: true,
    cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={22} />
  },
  ...(showPlotagemColumn ? [{
    id: 'plotagem',
    header: 'Plotagem',
    accessorKey: 'plotagem',
    size: 140,
    enableSorting: true,
    cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={15} />
  }] : []),
  {
    id: 'dataAgendamento',
    header: () => <HeaderLabel text="Agendamentos" />,
    accessorKey: 'dataAgendamento',
    size: 225,
    enableSorting: true,
    cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={55} />
  },
  {
    id: 'dataInicioCarregamento',
    header: () => <HeaderLabel text="Carregamentos" />,
    accessorKey: 'dataInicioCarregamento',
    size: 225,
    enableSorting: true,
    cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={55} />
  },
  /**
   *    {
    id: 'segmentoVeiculo',
    header: () => <HeaderLabel text="Segmento Veiculo" />,
    accessorKey: 'segmentoVeiculo',
    size: 92,
    enableSorting: true,
    cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={12} />
    },
   */

  ...(showTipoOperacaoColumn ? [{
    id: 'tipoOperacao',
    header: () => <HeaderLabel text="Tipo da Operacao" />,
    accessorKey: 'tipoOperacao',
    size: 130,
    enableSorting: true,
    cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={18} />
  }] : []),
  {
    id: 'cargas',
    header: 'Cargas',
    accessorKey: 'cargasResumo',
    size: 160,
    enableSorting: true,
    cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={18} />
  },
  {
    id: 'statusViagens',
    header: () => <HeaderLabel text="Status Viagem" />,
    accessorKey: 'statusViagensResumo',
    size: 150,
    enableSorting: true,
    cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={20} />
  },
  {
    id: 'disponivel',
    header: 'Status',
    accessorKey: 'disponivelLabel',
    size: 120,
    enableSorting: true,
    cell: ({ getValue }) => <CompactBadge label={getValue()} variant={getDisponibilidadeTmsBadgeVariant(getValue())} />
  },
  {
    id: 'mensagemDisponibilidade',
    header: () => <HeaderLabel text="Mensagem Disponibilidade" />,
    accessorKey: 'mensagemDisponibilidade',
    size: 220,
    enableSorting: true,
    cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={36} />
  },
  {
    id: 'statusPatio',
    header: 'Status Patio',
    accessorKey: 'statusPatioLabel',
    size: 120,
    enableSorting: true,
    cell: ({ getValue, row }) => <CompactBadge label={getValue()} variant={getStatusPatioBadgeVariant(row)} />
  }
];

const PositionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid #cfd8e3;
  border-radius: 3px;
  background: ${props => {
    if (props.$status === 'found') return '#d4edda';
    if (props.$status === 'stale') return '#fff3cd';
    if (props.$status === 'missing') return '#f8d7da';
    return '#ffffff';
  }};
  color: ${props => {
    if (props.$status === 'found') return '#155724';
    if (props.$status === 'stale') return '#856404';
    if (props.$status === 'missing') return '#721c24';
    return '#1f3f8f';
  }};
  cursor: pointer;

  &:hover {
    background: ${props => {
      if (props.$status === 'found') return '#c3e6cb';
      if (props.$status === 'stale') return '#ffeeba';
      if (props.$status === 'missing') return '#f5c6cb';
      return '#eef3ff';
    }};
  }
`;

const CompactBadgeWrap = styled.span`
  display: inline-block;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding: 3px 4px;
  box-sizing: border-box;
  overflow: hidden;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;
  border-radius: 0;
  font-size: 9px;
  font-weight: 600;
  line-height: 1.2;
  text-transform: uppercase;

  ${props => {
    switch (props.$variant) {
      case 'success':
        return `
          background-color: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        `;

      case 'danger':
        return `
          background-color: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        `;

      default:
        return `
          background-color: #e2e3e5;
          color: #383d41;
          border: 1px solid #d6d8db;
        `;
    }
  }}
`;

const HeaderLabelText = styled.span`
  display: inline-block;
  max-width: 100%;
  white-space: normal;
  line-height: 1.1;
  word-break: break-word;
`;
